import OpenPanelLeft16 from '@carbon/icons-react/es/open-panel--left/16';
import { useBoolean } from 'ahooks';
import { Row } from 'antd';
import { uniqBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PermissionSetType, ProjectId } from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import { Field, FieldWithValue } from 'api/tasks';
import { LinkButton } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { FieldSectionMapping } from 'components/organisms/field-sections/section-list';
import { computeSectionFieldMapping } from 'components/organisms/field-sections/utils';
import { canShowInProjectFieldsSettings } from 'components/organisms/module-settings/fields/utils';
import { ContainedList } from 'components/organisms/notifications/list/line-item/contained-list';
import { GhostFields } from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { addValuesToFields } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields';
import { HelperText01, Text02 } from 'components/styles';
import { useProjectDetail } from 'data/projects/hooks';
import {
  useCurrentUserProjectAccess,
  useUserPermissionSetType,
} from 'hooks/permissions';
import { useAccount } from 'hooks/use-account';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUpdateProjectMutation } from 'queries/project';
import { useProjectFieldSectionsQuery } from 'queries/project-field-sections';
import { useProjectFields } from 'utils/project-fields/use-project-fields';
import { useProjectFieldsAccess } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';
import { ResponsiveCard } from '../../../style';
import { ProjectOverviewExpanded } from './project-overview-expanded';
import { ProjectOverviewExpandedItem } from './project-overview-expanded-item';

const TOP_FIELDS_COUNT = 5;

const Card = styled(ResponsiveCard)`
  min-height: 320px;
  max-height: 320px;
`;
const Header = styled.div`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 54px;
    z-index: 0;
    background: var(--private-01);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;
const SubTitle = styled.p`
  ${HelperText01};
  ${Text02};
  display: flex;
  gap: var(--spacing-02);
`;
const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
`;

const StyledLinkButton = styled(LinkButton)`
  padding: var(--spacing-03) 0px;
`;

// Keeping the card height fixed so that see more fields is always visible
const StyledGhostFields = styled(GhostFields)`
  height: 200px;
  overflow-y: auto;
`;

const computeTopFields = (
  fieldSectionMapping: FieldSectionMapping[],
  topFieldsCount: number,
): [boolean, Field[]] => {
  const fieldsList = fieldSectionMapping.reduce((acc, section) => {
    return [...acc, ...section.fieldOrder];
  }, [] as Field[]);

  return [
    fieldsList.length > topFieldsCount,
    fieldsList.splice(0, topFieldsCount),
  ];
};

export const ProjectOverviewV2: FC<{ projectId: number }> = ({ projectId }) => {
  const [
    isProjectOverviewExpanded,
    {
      setTrue: openProjectOverviewExpanded,
      setFalse: closeProjectOverviewExpanded,
    },
  ] = useBoolean(false);
  const { data: fieldsList, isLoading: projectFieldsLoading } =
    useProjectFields();
  const { data: projectData, isLoading: projectDataLoading } = useProjectDetail(
    ProjectId(projectId),
  );
  const permissivePartnersInProject = useMemo(
    () =>
      (projectData?.partners ?? []).filter(
        ({ accessLevel }) =>
          accessLevel?.partnerPermissionSetName ===
          PartnerPermissionName.Permissive,
      ),
    [projectData?.partners],
  );

  const { data: fieldSections, isLoading: projectFieldSectionsLoading } =
    useProjectFieldSectionsQuery();
  const hasAccessToField = useProjectFieldsAccess();

  const projectFields = useMemo(() => {
    return fieldsList
      ? fieldsList
          .filter(canShowInProjectFieldsSettings)
          .filter(hasAccessToField)
          .filter((field) => field.isEnabled)
          .filter(
            (field) =>
              field.fieldType !== 'KEY_EVENTS' &&
              field.fieldType !== 'INTERVALS',
          )
      : [];
  }, [fieldsList, hasAccessToField]);

  const fieldsWithValues = useMemo(() => {
    return addValuesToFields(projectFields, projectData?.fields ?? []);
  }, [projectFields, projectData?.fields]);

  const fieldSectionMapping = useMemo(() => {
    return computeSectionFieldMapping(fieldsWithValues, fieldSections);
  }, [fieldsWithValues, fieldSections]);

  const [showMoreFields, projectTopFields] = useMemo(() => {
    return computeTopFields(fieldSectionMapping, TOP_FIELDS_COUNT);
  }, [fieldSectionMapping]);

  const { isProjectAdmin } = useCurrentUserProjectAccess(ProjectId(projectId));
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const isPartnerAdmin =
    currentUserPermissionSetType === PermissionSetType.PartnerAdmin;
  const canEdit = isAccountAdmin || isProjectAdmin || isPartnerAdmin;
  const isDefaultFieldsReadOnly = !canEdit;

  const { user } = useUser();
  const { company: vendorCompany } = useAccount();

  const errorMessage = useTranslation('error');
  const errorTitle = useTranslation('error_saving');
  const { mutate } = useUpdateProjectMutation(ProjectId(projectId));
  const handleOnCustomFieldChange = (
    fieldValue,
    { fieldId }: FieldWithValue,
  ) => {
    mutate(
      { fields: [{ fieldId, fieldValue }] },
      {
        onError: () => {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
        },
      },
    );
  };

  const handleOnDefaultFieldChange = (payload) => {
    mutate(payload, {
      onError: () => {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      },
    });
  };
  const sectionVisibleTo = useMemo(
    () =>
      uniqBy(
        [user.company, vendorCompany].concat(permissivePartnersInProject),
        'companyId',
      ).map(({ companyName }) => companyName),
    [user.company, permissivePartnersInProject, vendorCompany],
  );

  return (
    <Card
      data-cy="project.overview.project_info"
      title={
        <Header>
          <strong>
            <FormattedMessage id="project.overview.project_details.title" />
          </strong>
          <SubTitle>
            <FormattedMessage
              id="project.overview.project_details.sub_title"
              values={{
                companyName: (
                  <ContainedList
                    show={sectionVisibleTo.length}
                    items={sectionVisibleTo}
                  />
                ),
              }}
            />
          </SubTitle>
        </Header>
      }
    >
      {projectDataLoading ||
      projectFieldsLoading ||
      projectFieldSectionsLoading ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        projectData && (
          <>
            <StyledGhostFields>
              <Row gutter={[16, 4]}>
                {projectTopFields.map((field) => {
                  return (
                    <ProjectOverviewExpandedItem
                      field={field}
                      isReadOnly={isDefaultFieldsReadOnly}
                      values={projectData.fields}
                      onCustomFieldChange={handleOnCustomFieldChange}
                      onDefaultFieldChange={handleOnDefaultFieldChange}
                      projectData={projectData}
                    />
                  );
                })}
              </Row>
            </StyledGhostFields>
            {showMoreFields ? (
              <>
                <StyledLinkButton
                  onClick={openProjectOverviewExpanded}
                  icon={<OpenPanelLeft16 />}
                  data-cy="project.overview.see_more_fields"
                >
                  <FormattedMessage id="project.overview.see_more_fields" />
                </StyledLinkButton>
                <ProjectOverviewExpanded
                  projectData={projectData}
                  isDefaultFieldsReadOnly={isDefaultFieldsReadOnly}
                  fieldSectionMapping={fieldSectionMapping}
                  visible={isProjectOverviewExpanded}
                  closeDrawer={closeProjectOverviewExpanded}
                  onCustomFieldChange={handleOnCustomFieldChange}
                  onDefaultFieldChange={handleOnDefaultFieldChange}
                  visibleToText={
                    <ContainedList
                      show={sectionVisibleTo.length}
                      items={sectionVisibleTo}
                    />
                  }
                />
              </>
            ) : null}
          </>
        )
      )}
    </Card>
  );
};
